'use client';
import React, { useState } from 'react';
import {
  Anchor,
  TrendingUp,
  TrendingDown,
  Minus,
  Clock,
  Ship,
  AlertTriangle,
  CheckCircle2,
  RefreshCw,
  ChevronDown,
} from 'lucide-react';

interface PortData {
  id: string;
  name: string;
  code: string;
  state: string;
  congestionLevel: 'low' | 'moderate' | 'high' | 'critical';
  congestionPercent: number;
  vesselsBerth: number;
  vesselsAnchor: number;
  avgWaitHours: number;
  trend: 'improving' | 'stable' | 'worsening';
  lastUpdated: string;
  alerts: string[];
}

const PORTS: PortData[] = [
  {
    id: 'port-jnpt',
    name: 'Nhava Sheva (JNPT)',
    code: 'INNSA',
    state: 'Maharashtra',
    congestionLevel: 'high',
    congestionPercent: 78,
    vesselsBerth: 24,
    vesselsAnchor: 18,
    avgWaitHours: 36,
    trend: 'worsening',
    lastUpdated: '06/09/2026 15:00 IST',
    alerts: ['Gate congestion at Gate 4 — expect 2hr delays', 'Berth 11 under maintenance till 10/09'],
  },
  {
    id: 'port-mundra',
    name: 'Mundra Port',
    code: 'INMUN',
    state: 'Gujarat',
    congestionLevel: 'moderate',
    congestionPercent: 52,
    vesselsBerth: 18,
    vesselsAnchor: 9,
    avgWaitHours: 18,
    trend: 'stable',
    lastUpdated: '06/09/2026 14:30 IST',
    alerts: [],
  },
  {
    id: 'port-chennai',
    name: 'Chennai Port',
    code: 'INMAA',
    state: 'Tamil Nadu',
    congestionLevel: 'low',
    congestionPercent: 28,
    vesselsBerth: 12,
    vesselsAnchor: 4,
    avgWaitHours: 8,
    trend: 'improving',
    lastUpdated: '06/09/2026 14:00 IST',
    alerts: [],
  },
  {
    id: 'port-kolkata',
    name: 'Kolkata Port (KoPT)',
    code: 'INCCU',
    state: 'West Bengal',
    congestionLevel: 'critical',
    congestionPercent: 91,
    vesselsBerth: 16,
    vesselsAnchor: 27,
    avgWaitHours: 72,
    trend: 'worsening',
    lastUpdated: '06/09/2026 13:45 IST',
    alerts: ['Severe congestion — vessels waiting 3+ days', 'Haldia Dock: berth availability critical'],
  },
  {
    id: 'port-cochin',
    name: 'Cochin Port',
    code: 'INCOK',
    state: 'Kerala',
    congestionLevel: 'low',
    congestionPercent: 22,
    vesselsBerth: 8,
    vesselsAnchor: 2,
    avgWaitHours: 6,
    trend: 'improving',
    lastUpdated: '06/09/2026 14:15 IST',
    alerts: [],
  },
  {
    id: 'port-vizag',
    name: 'Visakhapatnam Port',
    code: 'INVTZ',
    state: 'Andhra Pradesh',
    congestionLevel: 'moderate',
    congestionPercent: 45,
    vesselsBerth: 10,
    vesselsAnchor: 6,
    avgWaitHours: 14,
    trend: 'stable',
    lastUpdated: '06/09/2026 13:30 IST',
    alerts: [],
  },
  {
    id: 'port-kandla',
    name: 'Deendayal Port (Kandla)',
    code: 'INKLA',
    state: 'Gujarat',
    congestionLevel: 'moderate',
    congestionPercent: 58,
    vesselsBerth: 14,
    vesselsAnchor: 11,
    avgWaitHours: 22,
    trend: 'worsening',
    lastUpdated: '06/09/2026 14:00 IST',
    alerts: ['Bulk cargo terminal at 90% capacity'],
  },
  {
    id: 'port-mormugao',
    name: 'Mormugao Port',
    code: 'INGOI',
    state: 'Goa',
    congestionLevel: 'low',
    congestionPercent: 18,
    vesselsBerth: 5,
    vesselsAnchor: 1,
    avgWaitHours: 4,
    trend: 'stable',
    lastUpdated: '06/09/2026 12:00 IST',
    alerts: [],
  },
];

const CONGESTION_CONFIG = {
  low: { label: 'Low', color: 'text-success', bg: 'bg-success', barBg: 'bg-success', cardBorder: 'border-success/20', icon: CheckCircle2 },
  moderate: { label: 'Moderate', color: 'text-warning', bg: 'bg-warning', barBg: 'bg-warning', cardBorder: 'border-warning/20', icon: Minus },
  high: { label: 'High', color: 'text-orange-500', bg: 'bg-orange-500', barBg: 'bg-orange-500', cardBorder: 'border-orange-200', icon: TrendingUp },
  critical: { label: 'Critical', color: 'text-danger', bg: 'bg-danger', barBg: 'bg-danger', cardBorder: 'border-danger/30', icon: AlertTriangle },
};

const TREND_CONFIG = {
  improving: { label: 'Improving', icon: TrendingDown, color: 'text-success' },
  stable: { label: 'Stable', icon: Minus, color: 'text-primary' },
  worsening: { label: 'Worsening', icon: TrendingUp, color: 'text-warning' },
};

export default function PortCongestion() {
  const [refreshing, setRefreshing] = useState(false);
  const [expandedPort, setExpandedPort] = useState<string | null>(null);
  const [filterLevel, setFilterLevel] = useState<'all' | 'low' | 'moderate' | 'high' | 'critical'>('all');

  const handleRefresh = () => {
    setRefreshing(true);
    // Backend integration point: fetch live port congestion data from port authority APIs
    setTimeout(() => setRefreshing(false), 1500);
  };

  const filtered = filterLevel === 'all' ? PORTS : PORTS.filter((p) => p.congestionLevel === filterLevel);
  const criticalCount = PORTS.filter((p) => p.congestionLevel === 'critical').length;
  const highCount = PORTS.filter((p) => p.congestionLevel === 'high').length;

  return (
    <div className="card-surface shadow-card overflow-hidden">
      {/* Header */}
      <div className="p-5 border-b border-border">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
              <Anchor size={18} className="text-primary" />
            </div>
            <div>
              <h2 className="text-base font-bold text-foreground flex items-center gap-2">
                Port Congestion Monitor
                <span className="inline-flex items-center gap-1 text-xs font-semibold bg-success/10 text-success border border-success/30 px-2 py-0.5 rounded-full">
                  <div className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
                  Live
                </span>
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Real-time congestion levels at major Indian ports
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {/* Filter */}
            <div className="relative">
              <select
                value={filterLevel}
                onChange={(e) => setFilterLevel(e.target.value as typeof filterLevel)}
                className="input-field h-8 pl-3 pr-7 text-xs font-medium appearance-none cursor-pointer"
              >
                <option value="all">All Ports</option>
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="moderate">Moderate</option>
                <option value="low">Low</option>
              </select>
              <ChevronDown size={11} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            </div>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground border border-border px-3 py-2 rounded-lg hover:bg-muted transition-colors disabled:opacity-50"
            >
              <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>
        </div>

        {/* Alert banner for critical ports */}
        {(criticalCount > 0 || highCount > 0) && (
          <div className="mt-3 flex items-center gap-2 bg-danger/10 border border-danger/30 rounded-lg px-4 py-2.5">
            <AlertTriangle size={14} className="text-danger shrink-0" />
            <p className="text-xs font-semibold text-danger">
              {criticalCount > 0 && <><span className="font-bold">{criticalCount} port{criticalCount > 1 ? 's' : ''}</span> at critical congestion. </>}
              {highCount > 0 && <><span className="font-bold">{highCount} port{highCount > 1 ? 's' : ''}</span> at high congestion. </>}
              Plan shipments accordingly.
            </p>
          </div>
        )}
      </div>

      {/* Port grid */}
      <div className="p-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {filtered.map((port) => {
          const cfg = CONGESTION_CONFIG[port.congestionLevel];
          const trendCfg = TREND_CONFIG[port.trend];
          const TrendIcon = trendCfg.icon;
          const StatusIcon = cfg.icon;
          const isExpanded = expandedPort === port.id;

          return (
            <div
              key={port.id}
              className={`rounded-xl border-2 ${cfg.cardBorder} bg-background overflow-hidden transition-all`}
            >
              {/* Card header */}
              <div
                className="p-4 cursor-pointer hover:bg-muted/30 transition-colors"
                onClick={() => setExpandedPort(isExpanded ? null : port.id)}
              >
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-foreground leading-tight">{port.name}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{port.code} · {port.state}</p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <span className={`inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full ${cfg.color} bg-current/10`}
                      style={{ backgroundColor: `color-mix(in srgb, currentColor 10%, transparent)` }}
                    >
                      <StatusIcon size={10} />
                      {cfg.label}
                    </span>
                    <ChevronDown size={14} className={`text-muted-foreground transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                  </div>
                </div>

                {/* Congestion bar */}
                <div className="mb-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-muted-foreground">Congestion Level</span>
                    <span className={`text-xs font-bold ${cfg.color}`}>{port.congestionPercent}%</span>
                  </div>
                  <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-700 ${cfg.barBg}`}
                      style={{ width: `${port.congestionPercent}%` }}
                    />
                  </div>
                </div>

                {/* Quick stats */}
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-1 text-muted-foreground mb-0.5">
                      <Ship size={10} />
                      <span>Berthed</span>
                    </div>
                    <p className="font-bold text-foreground">{port.vesselsBerth}</p>
                  </div>
                  <div className="text-center border-x border-border">
                    <div className="flex items-center justify-center gap-1 text-muted-foreground mb-0.5">
                      <Anchor size={10} />
                      <span>Anchored</span>
                    </div>
                    <p className="font-bold text-foreground">{port.vesselsAnchor}</p>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-1 text-muted-foreground mb-0.5">
                      <Clock size={10} />
                      <span>Avg Wait</span>
                    </div>
                    <p className={`font-bold ${port.avgWaitHours > 48 ? 'text-danger' : port.avgWaitHours > 24 ? 'text-warning' : 'text-success'}`}>
                      {port.avgWaitHours}h
                    </p>
                  </div>
                </div>
              </div>

              {/* Expanded details */}
              {isExpanded && (
                <div className="px-4 pb-4 border-t border-border pt-3 fade-in">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-muted-foreground">Trend</span>
                    <div className={`flex items-center gap-1 text-xs font-semibold ${trendCfg.color}`}>
                      <TrendIcon size={12} />
                      {trendCfg.label}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground mb-3">
                    <Clock size={10} />
                    <span>Updated: {port.lastUpdated}</span>
                  </div>
                  {port.alerts.length > 0 && (
                    <div className="flex flex-col gap-1.5">
                      {port.alerts.map((alert, i) => (
                        <div key={`alert-${port.id}-${i}`} className="flex items-start gap-2 bg-warning/10 border border-warning/20 rounded-lg px-3 py-2">
                          <AlertTriangle size={11} className="text-warning shrink-0 mt-0.5" />
                          <p className="text-xs text-warning font-medium">{alert}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  {port.alerts.length === 0 && (
                    <div className="flex items-center gap-2 bg-success/10 border border-success/20 rounded-lg px-3 py-2">
                      <CheckCircle2 size={11} className="text-success shrink-0" />
                      <p className="text-xs text-success font-medium">No active alerts for this port</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
